import {useCallback, useEffect, useRef, useState} from 'react';

import {detectServiceType} from '../utils/frameworkDetection';
import { devLog } from '../utils/devLog';
import { rememberedSelectionFor } from '../utils/urlWins';

/**
 * Owns `selectedResource` — the SINGLE effect below is its one real writer.
 *
 * Previously two separate effects both decided "what should selectedResource
 * be": an auto-select effect (fired on `configuration` change) and a "stuck
 * tab" retrigger (fired on `previewTabs`/`activeTab`/`selectedProject`/
 * `selectedResource` change) that existed purely to re-run the SAME
 * auto-select decision for the create-project-from-Home case, where a new
 * preview tab appears without `configuration` itself changing. Two effects
 * computing the same answer under different triggers is exactly the
 * "dual-writer" pattern this whole hook was extracted to eliminate (see the
 * DDD refactor plan). Merged into one: the guard/selection logic is
 * unchanged, it just now also re-runs when `previewTabs` or `activeTab`
 * change, which is what the stuck-tab case actually needed — a fresh
 * evaluation, not a second decision-maker.
 */
export function useProjectConfiguration({
  configuration,
  fullProjectConfig,
  selectedProject,
  activeTab,
  initialMicroservice,
  previewTabs,
  projectIdFromUrl,
  preferHome = false,
  // The service the URL names (`/code/<tenant>/<project>/<service>/<branch>/<tab>`), decoded; null at Home.
  serviceFromUrl = null,
}) {
  // Lazy-initialized from localStorage (not `null`) so the very first paint
  // already shows the service/tab the user was actually on — real-live
  // confirmed bug: hard-refreshing while on a service's Preview/Code view
  // flashed "Home" (this defaulting to `null`, read by UnifiedTopBar as "no
  // service selected") for a render or two before the auto-select effect
  // below (gated on `configuration` finishing its own async load) corrected
  // it — a visible flash-then-snap-back on every refresh. This optimistic
  // guess is unvalidated against `configuration` (the effect below still
  // re-checks it once that loads, exactly as it already did for a `null`
  // start, so a stale/deleted service still self-corrects the same way it
  // always has) — it IS validated against `projectIdFromUrl` (the route's
  // own `:projectId`, always known synchronously from useParams(), unlike
  // `selectedProject` which is itself fetched async) so a stored resource
  // from a DIFFERENT, previously-visited project is never shown even
  // optimistically — `mia_selectedResource` is one shared key across every
  // project, not scoped per-project.
  const [selectedResource, setSelectedResource] = useState(() => {
    try {
      // A project opened on its Dashboard (`?view=…`) starts on Home, not on the service last looked at.
      if (preferHome) return null;
      const stored = localStorage.getItem('mia_selectedResource');
      if (!stored) return null;
      // A URL naming a service wins: the remembered selection only if it is that very service (utils/urlWins.js).
      return rememberedSelectionFor(JSON.parse(stored), { projectIdFromUrl, serviceFromUrl });
    } catch {
      return null;
    }
  });

  // BUG FIX: names of previewable services this effect has already
  // auto-selected at least once for the CURRENT project — see the
  // `alreadyOnConfiguration` guard below for why this exists. Reset per
  // project so a genuinely new project starts fresh.
  const autoSelectedServiceNamesRef = useRef(new Set());
  // The configuration the project's entry was decided on (this project, and the project that configuration was loaded
  // for, Canvas's `loadedFor`): "land on Home" is about how a project is ENTERED, so it is decided on the first real
  // configuration of the project only (a service the agent creates later is picked up as usual). The previous
  // project's configuration still showing for a moment, or the empty stand-in Canvas sets while switching
  // (`placeholder`), is not that: deciding on it let the real one auto-select a service, and its preview start.
  const entryKeyRef = useRef(null);
  // The entry (entryKeyRef's key) that landed on Project Home, until a service is picked. While it holds, a selection
  // cleared under the same project and configuration (Canvas reloading the open project) goes back to Home: never to a
  // service's preview the person did not ask for, and never "Loading project…" for ever (nothing else re-runs the
  // effect below when only the selection is cleared).
  const landedHomeRef = useRef(null);
  useEffect(() => {
    autoSelectedServiceNamesRef.current = new Set();
  }, [projectIdFromUrl]);
  useEffect(() => {
    if (selectedResource?.type === 'service') { landedHomeRef.current = null; }
  }, [selectedResource]);

  const homeResource = () => ({
    type: 'configuration',
    data: {
      name: 'Project Configuration',
      repositoryUrl: fullProjectConfig?.repositoryUrl || fullProjectConfig?.configurationGitPath || selectedProject?.repositoryUrl || selectedProject?.configurationGitPath,
      repoUrl: fullProjectConfig?.repositoryUrl || fullProjectConfig?.configurationGitPath || selectedProject?.repositoryUrl || selectedProject?.configurationGitPath,
      type: 'configuration'
    }
  });

  // The selection was cleared while the project entered on Home is still the one shown: back to Home.
  const selectionCleared = !selectedResource;
  useEffect(() => {
    if (!selectionCleared || !preferHome || !configuration || configuration.placeholder) return;
    const entryKey = `${projectIdFromUrl || ''}|${configuration.loadedFor || ''}`;
    if (landedHomeRef.current !== entryKey || Object.keys(configuration.services || {}).length === 0) return;
    devLog('🏠 Selection cleared: back to project Home');
    setSelectedResource(homeResource());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionCleared]);

  // Auto-select first service when configuration is loaded (unless already have a selection or URL param)
  useEffect(() => {
    if (!configuration || !configuration.services || configuration.placeholder) return;

    const entryKey = `${projectIdFromUrl || ''}|${configuration.loadedFor || ''}`;
    const isEntry = entryKeyRef.current !== entryKey;
    entryKeyRef.current = entryKey;

    const services = Object.values(configuration.services);
    if (services.length === 0) return;

    // Only auto-select if:
    // 1. No service is currently selected, OR
    // 2. The currently selected service doesn't exist in the new configuration
    const currentServiceExists = selectedResource?.type === 'service' &&
      configuration.services[selectedResource.name];

    if (!selectedResource || !currentServiceExists) {
      // Don't auto-select if we have an initialMicroservice (URL will handle it)
      if (initialMicroservice) return;

      // Computed BEFORE the localStorage-restore check below (task #210) —
      // it used to run only in the fallback path further down, which meant
      // "restore whatever pseudo-resource/service was stored last time"
      // ALWAYS won, even once a real previewable service exists now that
      // didn't before. Live-confirmed: a chat bound to the "_root_"/Project
      // Configuration pseudo-resource (stored in `mia_selectedResource` the
      // first time this project was opened, before any service existed)
      // created a real service mid-conversation (`vfs_ensure_project`) —
      // but every re-run of this effect kept restoring the stale stored
      // 'configuration' selection instead of ever reaching the "find a
      // previewable service" logic, so the Preview panel's service dropdown
      // stayed on "Select service to preview..." forever and the build
      // never had anything real to target.
      const previewableService = (activeTab === 'preview' || activeTab === 'code')
        ? services.find(service => {
          if (service.vfsBacked) return true;
          const detection = detectServiceType(service);
          return detection.isFrontend && (service.repoUrl || service.sshUrl || service.repositoryUrl);
        })
        : null;

      // Once the user is explicitly on the Home/configuration pseudo-
      // resource, never resurrect a stale SERVICE from localStorage below —
      // this effect also re-runs on previewTabs/activeTab changes (for the
      // "stuck tab" case), and Home's own reset touches both, so without
      // this guard every Home click immediately restored whatever service
      // was last stored and undid the click (live-confirmed: "Go to Project
      // Home" from a service's Code/Preview view never actually left that
      // service). Still fall through to the previewableService check right
      // below so a real service that appears mid-conversation (e.g.
      // vfs_ensure_project) still gets auto-selected — that's the documented
      // regression this effect exists to avoid (see file header comment).
      const alreadyOnConfiguration = selectedResource?.type === 'configuration';

      // Check if there's a previously selected resource from localStorage
      const storedResource = !alreadyOnConfiguration && localStorage.getItem('mia_selectedResource');
      const storedProjectId = selectedProject?.id || selectedProject?.projectId || selectedProject?._id;

      if (storedResource && storedProjectId) {
        try {
          const parsed = JSON.parse(storedResource);
          // Only restore if it's for the same project and the resource still exists
          if (parsed.projectId === storedProjectId) {
            if (parsed.type === 'service' && configuration.services[parsed.data?.name]) {
              devLog('🔄 Restoring last selected service:', parsed.data?.name);
              setSelectedResource(parsed);
              return;
            } else if (parsed.type === 'configuration' && !previewableService) {
              // Only restore the pseudo-resource when there's still nothing
              // real to prefer instead — see `previewableService` comment
              // above for why this guard exists.
              devLog('🔄 Restoring last selected configuration');
              setSelectedResource(parsed);
              return;
            }
          }
        } catch (e) {
          console.warn('Failed to restore last selected resource:', e);
        }
      }

      // First-time entry (or a previewable service just appeared where the
      // stored resource above was skipped): if user is in preview or code
      // view, land on a service worth landing on. A vfs-backed service (no
      // repoUrl/sshUrl at all — see loadConfiguration's vfs-tree merge) is
      // just as valid a target as a git-backed frontend service: it's real,
      // it's what the Code tab's file tree already shows, and the whole
      // vfsSource render pipeline only fires once a real service (not the
      // "Project Configuration" pseudo-resource) is selected. Previously
      // this only matched git-backed services, so any vfs vfs-only
      // project always fell through to the configuration fallback below
      // regardless of having real services — the actual cause of the
      // preview panel staying on the wrong (non-previewable) pseudo-resource.
      // BUG FIX: this used to fire unconditionally whenever a
      // previewableService existed, even when `alreadyOnConfiguration` is
      // true — meaning EVERY re-run of this effect while the user was
      // explicitly on Home (it also re-runs on activeTab changes, and
      // Home's own click sets activeTab to 'preview' — see Canvas.jsx's
      // Home branch) silently re-selected that service and undid the
      // click. Live-confirmed: clicking "Home" from a service's Code/
      // Preview view landed correctly on
      // /code/<tenant>/<project>?conversationId=..., but clicking Home a
      // SECOND time bounced straight to
      // /code/<tenant>/<project>/services%2Fapp/main/preview?conversationId=...
      // — this effect re-running and "helpfully" re-selecting "app" the
      // instant the URL/state reflected Home. The comment above this
      // block explains why a bypass of `alreadyOnConfiguration` exists at
      // all: a service that appears for the FIRST time mid-conversation
      // (e.g. the very first vfs_ensure_project call) should still be
      // auto-selected even while sitting on the placeholder Home view.
      // Track which service names have already been offered once per
      // project — the FIRST time a given service appears, auto-select it
      // same as before; every later re-run, once the user has explicitly
      // backed out to Home, leave it alone.
      // The project was opened on its Dashboard (the URL says `?view=dashboard|files`): land on Home even though
      // there is a service to preview. The services that exist now count as already offered, so only a service that
      // appears later (the agent creating one mid-conversation) is still picked up.
      if (preferHome && isEntry) {
        services.forEach((service) => autoSelectedServiceNamesRef.current.add(service.name));
        landedHomeRef.current = entryKey;
        if (alreadyOnConfiguration) return;
        devLog('🏠 Landing on project Home');
        setSelectedResource(homeResource());
        return;
      }
      // Entered on Home and nothing picked since, but the selection was cleared (the open project reloaded): Home again,
      // not the first service's preview.
      if (preferHome && !selectedResource && landedHomeRef.current === entryKey) {
        devLog('🏠 Back to project Home');
        setSelectedResource(homeResource());
        return;
      }

      if (previewableService) {
        if (alreadyOnConfiguration && autoSelectedServiceNamesRef.current.has(previewableService.name)) {
          return;
        }
        devLog('🎯 Auto-selecting service for preview:', previewableService.name);
        autoSelectedServiceNamesRef.current.add(previewableService.name);
        setSelectedResource({type: 'service', data: previewableService, name: previewableService.name});
        return;
      }

      // Already on Home with nothing better to upgrade to — stay put rather
      // than calling setSelectedResource with a freshly-built (but
      // equivalent) configuration object on every re-run of this effect.
      if (alreadyOnConfiguration) return;

      // Fallback: Auto-select project configuration (configs) by default
      devLog('🎯 Auto-selecting project configuration (configs)');
      const configurationRepo = {
        name: 'Project Configuration',
        repositoryUrl: fullProjectConfig?.repositoryUrl || fullProjectConfig?.configurationGitPath || selectedProject?.repositoryUrl || selectedProject?.configurationGitPath,
        repoUrl: fullProjectConfig?.repositoryUrl || fullProjectConfig?.configurationGitPath || selectedProject?.repositoryUrl || selectedProject?.configurationGitPath,
        type: 'configuration'
      };
      setSelectedResource({type: 'configuration', data: configurationRepo});
    }
  // Re-run on previewTabs/activeTab too, not just configuration — a preview
  // tab can appear (e.g. right after Home creates a project) without
  // `configuration` itself changing, and this same guard/logic already
  // handles that: `!selectedResource` is still true, so it picks a
  // previewable service exactly as it would on a `configuration` change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration, previewTabs, activeTab]);

  // Goes to Project Home on purpose (a chat file link asking for the Files view): the services that exist now count as
  // already offered, so the effect above does not pick one of them again the moment Home's reset touches
  // previewTabs/activeTab (a project entered on a service's URL never offered any).
  const selectHome = useCallback((resource) => {
    Object.values(configuration?.services || {}).forEach((service) => autoSelectedServiceNamesRef.current.add(service.name));
    setSelectedResource(resource);
  }, [configuration]);

  return {selectedResource, setSelectedResource, selectHome};
}
